import React, { useState, useEffect } from 'react';
import {
  Store, Calendar, DollarSign, Plus, Trash2,
  Save, AlertCircle, Edit3, Tag, CreditCard,
  Banknote, TrendingDown, ShoppingBag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.synfin.ca';

// Fallback categories if API fails
const FALLBACK_CATEGORIES = [
  'Produce', 'Dairy', 'Meat', 'Bakery', 'Beverages',
  'Frozen', 'Pantry', 'Household', 'Personal Care',
  'Grocery', 'Other'
];

const ReceiptConfirmForm = ({ receiptData, onSave, onBack }) => {
  const [storeName, setStoreName]     = useState(receiptData.store_name || '');
  const [expenseDate, setExpenseDate] = useState(receiptData.expense_date || new Date().toISOString().split('T')[0]);
  const [subtotal, setSubtotal]       = useState(receiptData.subtotal || 0);
  const [tax, setTax]                 = useState(receiptData.tax || 0);
  const [total, setTotal]             = useState(receiptData.total || 0);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [categories, setCategories]   = useState(FALLBACK_CATEGORIES);

  // Initialise items preserving category from backend
  const [items, setItems] = useState(
    (receiptData.items || []).map(item => ({
      ...item,
      category: item.category || 'Other',
    }))
  );

  // Payment details from scan (read-only)
  const paymentMethod   = receiptData.payment_method   || 'Unknown';
  const paymentCardType = receiptData.payment_card_type || null;
  const cardLast4       = receiptData.payment_card_last4 || null;
  const cashTendered    = receiptData.cash_tendered     || null;
  const changeDue       = receiptData.change_due        || null;
  const totalSavings    = receiptData.total_savings     || 0;
  const roundedTotal    = receiptData.rounded_total     || null;
  const totalItemCount  = receiptData.total_item_count  || 0;

  // Load categories from backend DB
  useEffect(() => {
    fetch(`${API_URL}/api/categories?receipt_type=grocery`)
      .then(r => r.json())
      .then(res => {
        if (res.success && res.data && res.data.length > 0) {
          // Ensure all item categories are in the list
          const itemCats = (receiptData.items || [])
            .map(i => i.category)
            .filter(Boolean);
          const merged = [...new Set([...res.data, ...itemCats])].sort();
          setCategories(merged);
        }
      })
      .catch(() => {
        // Keep fallback categories on error
        const itemCats = (receiptData.items || [])
          .map(i => i.category)
          .filter(Boolean);
        const merged = [...new Set([...FALLBACK_CATEGORIES, ...itemCats])].sort();
        setCategories(merged);
      });
  }, [receiptData.items]);

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'quantity' || field === 'unit_price') {
      updated[index].total_price =
        parseFloat(updated[index].quantity || 1) *
        parseFloat(updated[index].unit_price || 0);
    }
    setItems(updated);
  };

  const addItem = () => {
    setItems([...items, {
      item_name: '', quantity: 1, unit_price: 0,
      total_price: 0, saving: 0, original_price: null, category: 'Other'
    }]);
  };

  const removeItem = (index) => setItems(items.filter((_, i) => i !== index));

  const recalcFromItems = () => {
    const itemsTotal = items.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);
    setSubtotal(Math.round(itemsTotal * 100) / 100);
    setTotal(Math.round((itemsTotal + parseFloat(tax || 0)) * 100) / 100);
  };

  const handleSave = async () => {
    setError('');
    if (!storeName.trim()) { setError('Please enter a store name.'); return; }
    if (!expenseDate) { setError('Please enter a date.'); return; }
    if (!total || parseFloat(total) <= 0) { setError('Please enter a valid total.'); return; }

    setSaving(true);
    await onSave({
      store_name: storeName.trim(),
      expense_date: expenseDate,
      subtotal: parseFloat(subtotal) || 0,
      tax: parseFloat(tax) || 0,
      total: parseFloat(total),
      currency: 'CAD',
      category: 'Groceries',
      total_savings: totalSavings,
      payment_method: paymentMethod,
      payment_card_type: paymentCardType,
      payment_card_last4: cardLast4,
      cash_tendered: cashTendered,
      change_due: changeDue,
      rounded_total: roundedTotal,
      items: items.filter(item => item.item_name?.trim()),
      imageFile: receiptData.imageFile,
      confidence: receiptData.confidence || 0,
    });
    setSaving(false);
  };

  const itemsTotal = items.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);
  const itemSavingsTotal = items.reduce((sum, item) => sum + (parseFloat(item.saving) || 0), 0);

  return (
    <div className="confirm-form">

      {/* Header */}
      <div className="confirm-header">
        <div>
          <h2 className="confirm-title">Review Receipt</h2>
          <p className="confirm-subtitle">
            Confidence: {receiptData.confidence || 0}% — Please review and correct any errors
          </p>
        </div>
        {receiptData.confidence > 0 && (
          <div className={`confidence-badge ${receiptData.confidence >= 70 ? 'confidence-high' : 'confidence-low'}`}>
            {receiptData.confidence >= 70 ? '✓ Good scan' : '⚠ Check details'}
          </div>
        )}
      </div>

      {error && <div className="confirm-error"><AlertCircle size={15} />{error}</div>}

      {/* Store Details */}
      <div className="confirm-section">
        <h3 className="confirm-section-title">Store Details</h3>
        <div className="confirm-fields-row">
          <div className="confirm-field">
            <label className="confirm-label"><Store size={13} /> Store Name</label>
            <input type="text" className="confirm-input"
              value={storeName} onChange={e => setStoreName(e.target.value)}
              placeholder="e.g. Walmart, Costco, Metro" maxLength={100} />
          </div>
          <div className="confirm-field confirm-field-sm">
            <label className="confirm-label"><Calendar size={13} /> Date</label>
            <input type="date" className="confirm-input"
              value={expenseDate} onChange={e => setExpenseDate(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Payment Details */}
      <div className="confirm-section">
        <h3 className="confirm-section-title"><CreditCard size={14} /> Payment Details</h3>
        <div className="payment-details-grid">
          <div className="payment-detail-card">
            <span className="payment-detail-label">Payment Method</span>
            <span className="payment-detail-value payment-method-badge">
              {paymentMethod}
              {paymentCardType && ` — ${paymentCardType}`}
              {cardLast4 && ` ****${cardLast4}`}
            </span>
          </div>
          {cashTendered !== null && cashTendered > 0 && (
            <div className="payment-detail-card">
              <span className="payment-detail-label"><Banknote size={13} /> Cash Tendered</span>
              <span className="payment-detail-value">${parseFloat(cashTendered).toFixed(2)}</span>
            </div>
          )}
          {changeDue !== null && changeDue > 0 && (
            <div className="payment-detail-card">
              <span className="payment-detail-label">Change Due</span>
              <span className="payment-detail-value">${parseFloat(changeDue).toFixed(2)}</span>
            </div>
          )}
          {roundedTotal !== null && (
            <div className="payment-detail-card">
              <span className="payment-detail-label">Rounded Total</span>
              <span className="payment-detail-value">${parseFloat(roundedTotal).toFixed(2)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Savings Summary */}
      {(totalSavings > 0 || itemSavingsTotal > 0) && (
        <div className="confirm-section savings-section">
          <h3 className="confirm-section-title"><TrendingDown size={14} /> Savings Summary</h3>
          <div className="savings-summary-grid">
            {totalSavings > 0 && (
              <div className="savings-card savings-card-total">
                <span className="savings-label">Total Savings</span>
                <span className="savings-amount">-${parseFloat(totalSavings).toFixed(2)}</span>
              </div>
            )}
            {itemSavingsTotal > 0 && (
              <div className="savings-card">
                <span className="savings-label">Item Discounts</span>
                <span className="savings-amount">-${itemSavingsTotal.toFixed(2)}</span>
              </div>
            )}
            {totalItemCount > 0 && (
              <div className="savings-card">
                <span className="savings-label"><ShoppingBag size={13} /> Total Units</span>
                <span className="savings-amount" style={{ color: 'var(--color-text-primary)' }}>
                  {totalItemCount}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Items */}
      <div className="confirm-section">
        <div className="confirm-section-header">
          <h3 className="confirm-section-title">
            Items ({items.length} lines · {totalItemCount} units)
          </h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="confirm-recalc-btn" onClick={recalcFromItems}>
              <Edit3 size={13} /> Recalc totals
            </button>
            <button className="confirm-add-btn" onClick={addItem}>
              <Plus size={13} /> Add item
            </button>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="confirm-empty-items">
            <p>No items detected. Add them manually.</p>
            <button className="confirm-add-btn" onClick={addItem}>
              <Plus size={13} /> Add item
            </button>
          </div>
        ) : (
          <div className="items-table">
            <div className="items-table-header items-table-header-extended">
              <span>Item Name</span>
              <span>Category</span>
              <span>Qty</span>
              <span>Unit $</span>
              <span>Total</span>
              <span>Saving</span>
              <span></span>
            </div>
            {items.map((item, index) => (
              <div key={index} className="items-table-row items-table-row-extended">
                <input type="text" className="item-input"
                  value={item.item_name}
                  onChange={e => updateItem(index, 'item_name', e.target.value)}
                  placeholder="Item name" />
                <select className="item-select"
                  value={item.category}
                  onChange={e => updateItem(index, 'category', e.target.value)}>
                  {/* Always include current category even if not in list */}
                  {!categories.includes(item.category) && item.category && (
                    <option value={item.category}>{item.category}</option>
                  )}
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <input type="number" className="item-input item-input-sm"
                  value={item.quantity} min="0" step="0.001"
                  onChange={e => updateItem(index, 'quantity', e.target.value)} />
                <input type="number" className="item-input item-input-sm"
                  value={item.unit_price} min="0" step="0.01"
                  onChange={e => updateItem(index, 'unit_price', e.target.value)} />
                <span className="item-total">
                  ${(parseFloat(item.total_price) || 0).toFixed(2)}
                </span>
                {parseFloat(item.saving) > 0 ? (
                  <span className="item-saving">-${parseFloat(item.saving).toFixed(2)}</span>
                ) : (
                  <span className="item-saving-none">—</span>
                )}
                <button className="item-remove-btn" onClick={() => removeItem(index)}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
            <div className="items-table-footer">
              <span>Items subtotal:</span>
              <span>${itemsTotal.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Totals */}
      <div className="confirm-section">
        <h3 className="confirm-section-title">Totals</h3>
        <div className="totals-grid">
          <div className="confirm-field">
            <label className="confirm-label"><DollarSign size={13} /> Subtotal</label>
            <input type="number" className="confirm-input"
              value={subtotal} min="0" step="0.01"
              onChange={e => setSubtotal(e.target.value)} />
          </div>
          <div className="confirm-field">
            <label className="confirm-label"><Tag size={13} /> Tax (HST/GST)</label>
            <input type="number" className="confirm-input"
              value={tax} min="0" step="0.01"
              onChange={e => setTax(e.target.value)} />
          </div>
          <div className="confirm-field">
            <label className="confirm-label total-label"><DollarSign size={13} /> Total Paid</label>
            <input type="number" className="confirm-input total-input"
              value={total} min="0" step="0.01"
              onChange={e => setTotal(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="confirm-actions">
        <button className="confirm-back-btn" onClick={onBack}>← Back</button>
        <button className="confirm-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? <span className="btn-spinner-sm" /> : <><Save size={16} /> Save Receipt</>}
        </button>
      </div>

    </div>
  );
};

export default ReceiptConfirmForm;
